import React, { useEffect } from 'react';
import { Box } from 'grommet';
import Toast from '../../components/Toast';
import { analytics } from '../../services/analytics';
import SHDImage from '@ui/Image/SHDImage';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDButton from '@ui/Button/SHDButton';
import SHDText from '@ui/Text/SHDText';
import useSportsRecruits from './useSportsRecruits';

interface SportsRecruitsAuthorizationModalProps {
  playerId: string;
  onClose: () => void;
  fromDeepLink?: boolean;
}

const SportsRecruitsAuthorizationModal: React.FC<
  SportsRecruitsAuthorizationModalProps
> = ({ playerId, onClose, fromDeepLink = false }) => {
  const {
    authorizeSRPlayer,
    fetchSrLandingPage,
    srUrl,
    error,
    loading,
    toast,
  } = useSportsRecruits(playerId);

  useEffect(() => {
    fetchSrLandingPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SHDThemeOverride full={false}>
      <SHDModalContainer>
        <Box alignSelf="center" pad={{ top: 'small', bottom: 'xxlarge' }}>
          <SHDImage
            width={200}
            src="/sr_shd_logos.png"
            alignSelf="center"
            alt="sr_shd_logo"
          />
        </Box>
        <Box gap="medium">
          <SHDHeading level={3} formatCasing={false}>
            Connect to <br />
            SportsRecruits
          </SHDHeading>
          <SHDText style="subhead" color="gray-700">
            Upon connecting sidelineHD to SportsRecruits, you’ll receive up to
            10 of your current best plays in your video library.
          </SHDText>
          <Box gap="none">
            <Box direction="row" gap={'xxsmall'}>
              <Box alignSelf="flex-start" margin={{ top: '2px' }}>
                <SHDImage
                  src="/PRO.svg"
                  width={'auto'}
                  height={16}
                  alt="sr_pro_logo"
                />
              </Box>
              <SHDText style="subhead" color="gray-700">
                subscription is required for automatic
              </SHDText>
            </Box>
            <SHDText style="subhead" color="gray-700">
              clip delivery after the initial connection.
            </SHDText>
          </Box>
        </Box>
      </SHDModalContainer>
      <SHDFooter>
        <SHDModalContainer>
          <Box pad={{ bottom: 'large' }} gap="xsmall">
            <SHDButton
              primary
              disabled={loading}
              label="Connect now"
              onClick={async () => {
                // admin needs to authorize the app first
                analytics.track('Connect to SportsRecruits Clicked', {
                  playerId,
                  fromDeepLink,
                });
                await authorizeSRPlayer(srUrl, onClose);
              }}
            />
            <SHDText size="footnote" color="gray-500" textAlign="center">
              By continuing, you are authorizing sidelineHD to share certain
              profile info with SportsRecruits
            </SHDText>
            {error && (
              <SHDText color="status-critical" style="legal">
                {error}
              </SHDText>
            )}
          </Box>
        </SHDModalContainer>
      </SHDFooter>
      {toast && <Toast {...toast} />}
    </SHDThemeOverride>
  );
};

export default SportsRecruitsAuthorizationModal;
